import React, { useState, useEffect } from "react";
import { Form, FormGroup, Input, Label, Row, Col, Button, Card, CardBody } from "reactstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import API_URL from '../config';
import { saveEmployee } from "./Api/GmsEmployee";
import { User, Phone, Mail, Calendar, MapPin, Building2, Briefcase, FileText, CreditCard, IdCard, ShieldCheck, Gem, Info } from "lucide-react";

// Form helper components
const FormSelect = ({ label, id, name, options, value, onChange, error, required = true, isDisabled = false, icon: Icon }) => (
  <div className="form-group mb-2">
    <Label className="col-form-label pb-0" htmlFor={id} style={{ fontSize: '12px', fontWeight: '600', color: '#495057', marginBottom: '2px' }}>
      {label} {required && <span style={{ color: '#dc3545' }}>*</span>}
    </Label>
    <div style={{ position: 'relative' }}>
      {Icon && (
        <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', zIndex: 2, color: '#6c757d', pointerEvents: 'none' }}>
          <Icon size={16} />
        </div>
      )}
      <Select
        id={id}
        name={name}
        options={options}
        value={value}
        onChange={onChange}
        isClearable={!isDisabled}
        isSearchable
        isDisabled={isDisabled}
        placeholder={`Select ${label}`}
        classNamePrefix="select2-selection"
        className={error ? 'is-invalid' : ''}
        styles={{
          control: (provided, state) => ({
            ...provided,
            backgroundColor: isDisabled ? '#f8f9fa' : '#fff',
            borderColor: error ? '#dc3545' : (state.isFocused ? '#556ee6' : '#ced4da'),
            boxShadow: state.isFocused && !error ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
            minHeight: '35px',
            borderRadius: '5px',
            fontSize: '13px',
            paddingLeft: Icon ? '35px' : '0px',
            '&:hover': { borderColor: error ? '#dc3545' : '#556ee6' }
          }),
          menu: (provided) => ({ ...provided, borderRadius: '6px', zIndex: 9999 }),
          menuPortal: (base) => ({ ...base, zIndex: 99999 }),
          option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
            color: state.isSelected ? 'white' : '#495057',
            fontSize: '13px',
            padding: '8px 12px'
          }),
          placeholder: (provided) => ({ ...provided, fontSize: '13px' }),
          singleValue: (provided) => ({ ...provided, fontSize: '13px' })
        }}
        menuPortalTarget={document.body}
      />
    </div>
    {error && <div className="invalid-feedback d-block" style={{ fontSize: '12px' }}>{error}</div>}
  </div>
);

const FormInput = ({ label, id, name, type = "text", value, onChange, error, required = true, placeholder, disabled = false, readOnly = false, className = "", icon: Icon }) => (
  <div className="form-group mb-2">
    <Label className="col-form-label pb-0" htmlFor={id} style={{ fontSize: '12px', fontWeight: '600', color: '#495057', marginBottom: '2px' }}>
      {label} {required && <span style={{ color: '#dc3545' }}>*</span>}
    </Label>
    <div style={{ position: 'relative' }}>
      {Icon && (
        <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', zIndex: 1, color: '#6c757d', display: 'flex', alignItems: 'center' }}>
          <Icon size={16} />
        </div>
      )}
      <Input
        type={type}
        className={`form-control ${error ? 'is-invalid' : ''} ${className}`}
        id={id}
        name={name}
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        style={{
          paddingLeft: Icon ? '35px' : '12px',
          borderRadius: '5px',
          fontSize: '13px',
          height: '35px',
          borderColor: error ? '#dc3545' : '#ced4da'
        }}
      />
    </div>
    {error && <div className="invalid-feedback d-block" style={{ fontSize: '12px' }}>{error}</div>}
  </div>
);

const FormCheckbox = ({ label, id, name, checked, onChange, disabled = false }) => (
  <div className="form-group mb-2">
    <div className="form-check form-switch p-0" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <input
        type="checkbox"
        className="form-check-input ms-0"
        id={id}
        name={name}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        style={{ cursor: 'pointer', float: 'none' }}
      />
      <label className="form-check-label" htmlFor={id} style={{ fontSize: '13px', fontWeight: '500', cursor: 'pointer', margin: 0 }}>
        {label === "Active" ? (checked ? "Active" : "Inactive") : label}
      </label>
    </div>
  </div>
);

const FormBenefitCheckbox = ({ label, checkboxName, amountName, checkboxChecked, amountValue, onCheckboxChange, onAmountChange }) => (
  <div className="benefit-item">
    <div className="form-check">
      <Input
        type="checkbox"
        className="form-check-input"
        id={checkboxName}
        name={checkboxName}
        checked={checkboxChecked}
        onChange={onCheckboxChange}
      />
      <Label className="form-check-label" htmlFor={checkboxName}>
        {label}
      </Label>
    </div>
    {checkboxChecked && (
      <div className="mt-2">
        <Input
          type="number"
          name={amountName}
          value={amountValue || 0}
          onChange={onAmountChange}
          placeholder="Enter amount"
          min="0"
          step="any"
          className="form-control-sm"
        />
      </div>
    )}
  </div>
);

const ImagePreview = ({ file, existingImage }) => {
  if (!file && !existingImage) return null;

  let imageUrl;
  if (file) {
    imageUrl = URL.createObjectURL(file);
  } else if (existingImage) {
    imageUrl = existingImage.startsWith('http') ? existingImage : `${API_URL}${existingImage}`;
  }
  
  return (
    <div className="mt-2">
      <img 
        src={imageUrl} 
        alt="Employee" 
        style={{ 
          maxWidth: '200px', 
          maxHeight: '200px', 
          objectFit: 'cover',
          borderRadius: '8px'
        }} 
      />
    </div>
  );
};



const EmployeeForm = ({
  form,
  setForm,
  errors,
  setErrors,
  loading,
  setLoading,
  isEditing = false,
  onSuccess,
  onCancel,
  genders = [],
  countries = [],
  businesses = [],
  departments = [],
  designations = [],
  branches = [],
  hasDeletePermission = false,
  isCompanyDisabled = false,
  isBranchDisabled = false
}) => {
  const [selectedImage, setSelectedImage] = useState(null);

  // Generate options for dropdowns
  const genderOptions = genders.map(gender => ({
    value: gender.gender_Id,
    label: `${gender.gender_NameEn} - ${gender.gender_NameAr}`
  }));

  const countryOptions = countries.map(country => ({
    value: country.country_Id,
    label: `${country.country_NameEn} - ${country.country_NameAr}`
  }));

  const businessOptions = businesses.map(business => ({
    value: business.business_Id,
    label: `${business.business_NameEn} - ${business.business_NameAr}`
  }));

  const departmentOptions = departments.map(dept => ({
    value: dept.department_Id,
    label: `${dept.department_NameEn} - ${dept.department_NameAr}`
  }));

  const designationOptions = designations.map(des => ({
    value: des.designation_Id,
    label: `${des.designation_NameEn} - ${des.designation_NameAr}`
  }));

  // Branches are already filtered by business in the parent component
  const branchOptions = branches.map(branch => ({
    value: branch.branch_Id,
    label: `${branch.branch_NameEn} - ${branch.branch_NameAr}`
  }));

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    let processedValue = type === "checkbox" ? checked : value;

    // Handle numeric fields
    if (['employee_BS', 'employee_HRA', 'employee_TA', 'employee_OA', 'wpsSalary',
         'employee_FoodAllowance', 'employee_CommunicationAllowance', 'employee_UtilityAllowance',
         'employee_HousingAmount', 'employee_TelephoneAmount', 'employee_FoodAmount', 'employee_OtherBenefitsAmount'].includes(name)) {
      if (value < 0) {
        setErrors(prev => ({
          ...prev,
          [name]: "Amount cannot be negative"
        }));
        return;
      }
      processedValue = value === '' ? 0 : parseFloat(value) || 0;

      if (name === 'wpsSalary') {
        const currentGrossSalary = 
          Number(form.employee_BS || 0) +
          Number(form.employee_HRA || 0) +
          Number(form.employee_TA || 0) +
          Number(form.employee_OA || 0) +
          Number(form.employee_FoodAllowance || 0) +
          Number(form.employee_CommunicationAllowance || 0) +
          Number(form.employee_UtilityAllowance || 0);

        if (processedValue > currentGrossSalary) {
          setErrors(prev => ({
            ...prev,
            [name]: "WPS Salary cannot exceed Gross Salary"
          }));
          return;
        }
      }
    }

    const updatedForm = { ...form, [name]: processedValue };

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    if (['employee_BS', 'employee_HRA', 'employee_TA', 'employee_OA',
         'employee_FoodAllowance', 'employee_CommunicationAllowance', 'employee_UtilityAllowance'].includes(name)) {
      const grossSalary = 
        Number(updatedForm.employee_BS || 0) +
        Number(updatedForm.employee_HRA || 0) +
        Number(updatedForm.employee_TA || 0) +
        Number(updatedForm.employee_OA || 0) +
        Number(updatedForm.employee_FoodAllowance || 0) +
        Number(updatedForm.employee_CommunicationAllowance || 0) +
        Number(updatedForm.employee_UtilityAllowance || 0);

      updatedForm.employee_GS = grossSalary;

      if (Number(updatedForm.wpsSalary || 0) > grossSalary) {
        setErrors(prev => ({
          ...prev,
          wpsSalary: "WPS Salary cannot exceed Gross Salary"
        }));
        updatedForm.wpsSalary = grossSalary;
      }
    }

    setForm(updatedForm);
  };

  const handleSelectFormChange = (selectedOption, field) => {
    setForm(prevForm => {
      const updatedForm = {
        ...prevForm,
        [field]: selectedOption ? selectedOption.value : null
      };
      
      if (field === 'employee_Business_Id') {
        updatedForm.employee_Branch_Id = null;
      }
      
      return updatedForm;
    });
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedImage(file);
    }
  };

  const copyAddress1ToAddress2 = () => {
    setForm(prevForm => ({
      ...prevForm,
      employee_Address2: prevForm.employee_Address1
    }));
  };

  const validateMandatoryFields = () => {
    const newErrors = {};

    if (!form.employee_NameEn?.trim()) {
      newErrors.employee_NameEn = "Employee Name (English) is required";
    }
    if (!form.emp_No?.trim()) {
      newErrors.emp_No = "Employee Number is required";
    }
    if (form.emp_No && form.emp_No.length > 10) {
      newErrors.emp_No = "Employee Number cannot exceed 10 characters";
    }
    if (!form.employee_Gender_Id) {
      newErrors.employee_Gender_Id = "Gender is required";
    }
    if (!form.employee_ContactNumber1?.trim()) {
      newErrors.employee_ContactNumber1 = "Primary Contact Number is required";
    }
    if (!form.employee_NatID?.trim()) {
      newErrors.employee_NatID = "National ID is required";
    }
    if (!form.employee_NatID_ExpDate) {
      newErrors.employee_NatID_ExpDate = "National ID Expiry Date is required";
    }
    if (!form.employee_PassportNumber?.trim()) {
      newErrors.employee_PassportNumber = "Passport Number is required";
    }
    if (!form.employee_Passport_ExpDate) {
      newErrors.employee_Passport_ExpDate = "Passport Expiry Date is required";
    }
    if (!form.employee_VisaNumber?.trim()) {
      newErrors.employee_VisaNumber = "Visa Number is required";
    }
    if (!form.employee_Visa_ExpDate) {
      newErrors.employee_Visa_ExpDate = "Visa Expiry Date is required";
    }
    if (!form.employee_LabourCardNumber?.trim()) {
      newErrors.employee_LabourCardNumber = "Labour Card Number is required";
    }
    if (!form.employee_LabourCardExpiry) {
      newErrors.employee_LabourCardExpiry = "Labour Card Expiry Date is required";
    }
    if (!form.employee_JoinDate) {
      newErrors.employee_JoinDate = "Joining Date is required";
    }
    if (!form.employee_BS || form.employee_BS <= 0) {
      newErrors.employee_BS = "Basic Salary is required and must be greater than 0";
    }
    if (!form.employee_HRA || form.employee_HRA < 0) {
      newErrors.employee_HRA = "HRA is required and cannot be negative";
    }
    if (!form.wpsSalary || form.wpsSalary <= 0) {
      newErrors.wpsSalary = "WPS Salary is required and must be greater than 0";
    }
    if (!form.employee_Address1?.trim()) {
      newErrors.employee_Address1 = "Address 1 is required";
    }
    if (!form.employee_Business_Id) {
      newErrors.employee_Business_Id = "Business is required";
    }
    if (!form.employee_Department_ID) {
      newErrors.employee_Department_ID = "Department is required";
    }
    if (!form.employee_Designation_ID) {
      newErrors.employee_Designation_ID = "Designation is required";
    }
    if (!form.employee_Branch_Id) {
      newErrors.employee_Branch_Id = "Branch is required";
    }
    if (!form.employee_BirthDate) {
      newErrors.employee_BirthDate = "Birth Date is required";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (loading) {
      return;
    }
    
    setLoading(true);
    
    const validationErrors = validateMandatoryFields();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      Swal.fire("Validation Error", "Please fill in all mandatory fields", "error");
      setLoading(false);
      return;
    }
    
    try {
      const formData = new FormData();
      
      if (selectedImage) {
        formData.append('imageFile', selectedImage);
      } else if (isEditing && form.employee_ImagePath) {
        const dummyFile = new File([''], 'dummy.txt', { type: 'text/plain' });
        formData.append('imageFile', dummyFile);
        formData.append('keepExistingImage', 'true');
      }
      
      const fieldMapping = {
        'employee_Id': 'Employee_Id',
        'employee_Code': 'Employee_Code', 
        'employee_NameEn': 'Employee_NameEn',
        'employee_NameAr': 'Employee_NameAr',
        'employee_Gender_Id': 'Employee_Gender_Id',
        'employee_BirthDate': 'Employee_BirthDate',
        'employee_JoinDate': 'Employee_JoinDate',
        'employee_PassportNumber': 'Employee_PassportNumber',
        'employee_NatID': 'Employee_NatID',
        'employee_VisaNumber': 'Employee_VisaNumber',
        'employee_Passport_Date': 'Employee_Passport_Date',
        'employee_Passport_ExpDate': 'Employee_Passport_ExpDate',
        'employee_NatID_Date': 'Employee_NatID_Date',
        'employee_NatID_ExpDate': 'Employee_NatID_ExpDate',
        'employee_Visa_Date': 'Employee_Visa_Date',
        'employee_Visa_ExpDate': 'Employee_Visa_ExpDate',
        'employee_Country_Id': 'Employee_Country_Id',
        'employee_Address1': 'Employee_Address1',
        'employee_Address2': 'Employee_Address2',
        'employee_Department_ID': 'Employee_Department_ID',
        'employee_Designation_ID': 'Employee_Designation_ID',
        'employee_ServiceEndDate': 'Employee_ServiceEndDate',
        'employee_CreatedDate': 'Employee_CreatedDate',
        'employee_CreatedBy': 'Employee_CreatedBy',
        'employee_ModifiedDate': 'Employee_ModifiedDate',
        'employee_ModifiedBy': 'Employee_ModifiedBy',
        'employee_Business_Id': 'Employee_Business_Id',
        'employee_Branch_Id': 'Employee_Branch_Id',
        'employee_BS': 'Employee_BS',
        'employee_HRA': 'Employee_HRA',
        'employee_TA': 'Employee_TA',
        'employee_OA': 'Employee_OA',
        'employee_GS': 'Employee_GS',
        'employee_IsOnProbation': 'Employee_IsOnProbation',
        'employee_ImagePath': 'Employee_ImagePath',
        'employee_ContactNumber1': 'Employee_ContactNumber1',
        'employee_ContactNumber2': 'Employee_ContactNumber2',
        'employee_Email': 'Employee_Email',
        'employee_LabourCardNumber': 'Employee_LabourCardNumber',
        'employee_LabourCardExpiry': 'Employee_LabourCardExpiry',
        'employee_EmploymentType': 'Employee_EmploymentType',
        'employee_FoodAllowance': 'Employee_FoodAllowance',
        'employee_CommunicationAllowance': 'Employee_CommunicationAllowance',
        'employee_UtilityAllowance': 'Employee_UtilityAllowance',
        'employee_HousingProvided': 'Employee_HousingProvided',
        'employee_HousingAmount': 'Employee_HousingAmount',
        'employee_TelephoneProvided': 'Employee_TelephoneProvided',
        'employee_TelephoneAmount': 'Employee_TelephoneAmount',
        'employee_FoodProvided': 'Employee_FoodProvided',
        'employee_FoodAmount': 'Employee_FoodAmount',
        'employee_OtherBenefits': 'Employee_OtherBenefits',
        'employee_OtherBenefitsAmount': 'Employee_OtherBenefitsAmount',
        'onWork': 'OnWork',
        'emp_No': 'Emp_No',
        'wpsSalary': 'WpsSalary'
      };

      Object.keys(form).forEach(key => {
        const backendFieldName = fieldMapping[key] || key;
        
        if (key.includes('Date')) {
          if (form[key]) {
            const dateValue = new Date(form[key]).toISOString();
            formData.append(backendFieldName, dateValue);
          } else {
            if (['employee_CreatedDate', 'employee_ModifiedDate'].includes(key)) {
              const currentDate = new Date().toISOString();
              formData.append(backendFieldName, currentDate);
            } else {
              formData.append(backendFieldName, '');
            }
          }
        } else {
          let value = form[key];
          
          if (['employee_Business_Id', 'employee_Department_ID', 'employee_Designation_ID', 
               'employee_Branch_Id', 'employee_Gender_Id', 'employee_Country_Id',
               'employee_CreatedBy', 'employee_ModifiedBy'].includes(key)) {
            value = value || 0;
          }
          
          if (['employee_IsOnProbation', 'onWork', 'employee_HousingProvided', 
               'employee_TelephoneProvided', 'employee_FoodProvided', 'employee_OtherBenefits'].includes(key)) {
            value = value === true || value === 'true' ? true : false;
          }
          
          if (['employee_BS', 'employee_HRA', 'employee_TA', 'employee_OA', 'employee_GS',
               'wpsSalary', 'employee_FoodAllowance', 'employee_CommunicationAllowance',
               'employee_UtilityAllowance', 'employee_HousingAmount', 'employee_TelephoneAmount',
               'employee_FoodAmount', 'employee_OtherBenefitsAmount'].includes(key)) {
            value = value || 0;
          }
          
          formData.append(backendFieldName, value !== null && value !== undefined ? value : '');
        }
      });
      
      const response = await fetch(`${API_URL}/api/Employee/save`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          const { handleSessionTimeout } = await import('./Api/SessionTimeoutHandler');
          handleSessionTimeout();
          throw new Error('Session timed out. Please login again.');
        }
        
        let errorMessage = 'Failed to save employee';
        try {
          const errorResponse = await response.json();
          errorMessage = errorResponse.msg || errorResponse.message || errorMessage;
        } catch (e) {
          console.error('Could not parse error response:', e);
        }
        
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      
      if (result.id) {
        Swal.fire("Success", result.msg || "Employee saved successfully", "success");
        if (onSuccess) {
          onSuccess();
        }
      } else {
        Swal.fire("Error", result.msg || "Failed to save employee", "error");
      }
    } catch (error) {
      console.error('Error saving employee:', error);
      Swal.fire("Error", `Failed to save employee: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {/* Personal Information Section */}
      <Card className="mb-3" style={{ border: '2px solid #556ee6', borderRadius: '10px', boxShadow: '0 2px 8px rgba(85,110,230,0.08)', overflow: 'hidden' }}>
        <CardBody className="p-3">
          <div className="d-flex align-items-center mb-3 pb-2 border-bottom">
            <div style={{ width: '28px', height: '28px', borderRadius: '5px', backgroundColor: 'rgba(85, 110, 230, 0.1)', color: '#556ee6', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
              <User size={16} />
            </div>
            <h6 className="mb-0" style={{ fontWeight: 'bold', fontSize: '14px', color: '#556ee6', fontFamily: "'Poppins', sans-serif" }}>Personal Information</h6>
          </div>
          <Row className="mb-3">
            <Col lg="3">
              <FormInput
                label="Employee Number"
                id="emp_No"
                name="emp_No"
                value={form.emp_No || ''}
                onChange={handleFormChange}
                placeholder="ID Number"
                maxLength={10}
                required={true}
                error={errors.emp_No}
              />
            </Col>

            <Col lg="3">
              <FormInput
                label="Name (English)"
                id="employee_NameEn"
                name="employee_NameEn"
                value={form.employee_NameEn}
                onChange={handleFormChange}
                error={errors.employee_NameEn}
                placeholder="Name in English"
              />
            </Col>

            <Col lg="3">
              <FormInput
                label="Name (Arabic)"
                id="employee_NameAr"
                name="employee_NameAr"
                value={form.employee_NameAr}
                onChange={handleFormChange}
                error={errors.employee_NameAr}
                placeholder="Name in Arabic"
                required={false}
              />
            </Col>

            <Col lg="3">
              <FormSelect
                label="Gender"
                id="employee_Gender_Id"
                name="employee_Gender_Id"
                options={genderOptions}
                value={genderOptions.find(option => option.value === form.employee_Gender_Id) || null}
                onChange={(selectedOption) => handleSelectFormChange(selectedOption, 'employee_Gender_Id')}
                error={errors.employee_Gender_Id}
              />
            </Col>
          </Row>

          <Row className="mb-3 align-items-center">
            <Col lg="3">
              <FormInput
                label="Birth Date"
                id="employee_BirthDate"
                name="employee_BirthDate"
                type="date"
                value={form.employee_BirthDate}
                onChange={handleFormChange}
                error={errors.employee_BirthDate}
                required={true}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                label="Nationality"
                id="employee_Country_Id"
                name="employee_Country_Id"
                options={countryOptions}
                value={countryOptions.find(option => option.value === form.employee_Country_Id) || null}
                onChange={(selectedOption) => handleSelectFormChange(selectedOption, 'employee_Country_Id')}
                error={errors.employee_Country_Id}
                required={false}
              />
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Contact & Identity Section */}
      <Card className="mb-3" style={{ border: '2px solid #17a2b8', borderRadius: '10px', boxShadow: '0 2px 8px rgba(23,162,184,0.08)', overflow: 'hidden' }}>
        <CardBody className="p-3">
          <div className="d-flex align-items-center mb-3 pb-2 border-bottom">
            <div style={{ width: '28px', height: '28px', borderRadius: '5px', backgroundColor: 'rgba(23, 162, 184, 0.1)', color: '#17a2b8', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
              <Phone size={16} />
            </div>
            <h6 className="mb-0" style={{ fontWeight: 'bold', fontSize: '14px', color: '#17a2b8', fontFamily: "'Poppins', sans-serif" }}>Contact & Identity</h6>
          </div>
          <Row className="mb-3">
            <Col lg="4">
              <FormInput
                label="Primary Contact"
                id="employee_ContactNumber1"
                name="employee_ContactNumber1"
                type="tel"
                value={form.employee_ContactNumber1}
                onChange={(e) => {
                  const numericValue = e.target.value.replace(/\D/g, '');
                  handleFormChange({ ...e, target: { ...e.target, name: e.target.name, value: numericValue } });
                }}
                error={errors.employee_ContactNumber1}
                placeholder="Primary Contact"
                required={true}
                icon={Phone}
              />
            </Col>
            <Col lg="4">
              <FormInput
                label="Secondary Contact"
                id="employee_ContactNumber2"
                name="employee_ContactNumber2"
                type="tel"
                value={form.employee_ContactNumber2}
                onChange={(e) => {
                  const numericValue = e.target.value.replace(/\D/g, '');
                  handleFormChange({ ...e, target: { ...e.target, name: e.target.name, value: numericValue } });
                }}
                error={errors.employee_ContactNumber2}
                placeholder="Secondary Contact"
                required={false}
                icon={Phone}
              />
            </Col>
            <Col lg="4">
              <FormInput
                label="Email"
                id="employee_Email"
                name="employee_Email"
                type="email"
                value={form.employee_Email}
                onChange={handleFormChange}
                error={errors.employee_Email}
                placeholder="Enter Email"
                required={false}
                icon={Mail}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col lg="4">
              <FormInput
                label="National ID"
                id="employee_NatID"
                name="employee_NatID"
                value={form.employee_NatID}
                onChange={handleFormChange}
                error={errors.employee_NatID}
                placeholder="National ID"
                icon={IdCard}
              />
            </Col>
            <Col lg="4">
              <FormInput
                label="National ID Issue"
                id="employee_NatID_Date"
                name="employee_NatID_Date"
                type="date"
                value={form.employee_NatID_Date}
                onChange={handleFormChange}
                error={errors.employee_NatID_Date}
                required={false}
                icon={Calendar}
              />
            </Col>
            <Col lg="4">
              <FormInput
                label="National ID Expiry"
                id="employee_NatID_ExpDate"
                name="employee_NatID_ExpDate"
                type="date"
                value={form.employee_NatID_ExpDate}
                onChange={handleFormChange}
                error={errors.employee_NatID_ExpDate}
                icon={Calendar}
              />
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Address Information Section */}
      <Card className="mb-3" style={{ border: '2px solid #ffc107', borderRadius: '10px', boxShadow: '0 2px 8px rgba(255,193,7,0.08)', overflow: 'hidden' }}>
        <CardBody className="p-3">
          <div className="d-flex align-items-center mb-3 pb-2 border-bottom">
            <div style={{ width: '28px', height: '28px', borderRadius: '5px', backgroundColor: 'rgba(255, 193, 7, 0.1)', color: '#ffc107', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
              <MapPin size={16} />
            </div>
            <h6 className="mb-0" style={{ fontWeight: 'bold', fontSize: '14px', color: '#ffc107', fontFamily: "'Poppins', sans-serif" }}>Address Information</h6>
          </div>
          <Row className="mb-3">
            <Col lg="6">
              <FormInput
                label="Address 1"
                id="employee_Address1"
                name="employee_Address1"
                value={form.employee_Address1}
                onChange={handleFormChange}
                error={errors.employee_Address1}
                placeholder="Primary Address"
                required={true}
                icon={MapPin}
              />
              <Button
                color="secondary"
                outline
                size="sm"
                onClick={copyAddress1ToAddress2}
                className="mt-1"
                type="button"
                style={{ borderRadius: '5px', fontSize: '11px', fontWeight: '600' }}
              >
                Copy to Address 2
              </Button>
            </Col>
            <Col lg="6">
              <FormInput
                label="Address 2"
                id="employee_Address2"
                name="employee_Address2"
                value={form.employee_Address2}
                onChange={handleFormChange}
                error={errors.employee_Address2}
                placeholder="Secondary Address"
                required={false}
                icon={MapPin}
              />
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Employment Details Section */}
      <Card className="mb-3" style={{ border: '2px solid #6610f2', borderRadius: '10px', boxShadow: '0 2px 8px rgba(102,16,242,0.08)', overflow: 'hidden' }}>
        <CardBody className="p-3">
          <div className="d-flex align-items-center mb-3 pb-2 border-bottom">
            <div style={{ width: '28px', height: '28px', borderRadius: '5px', backgroundColor: 'rgba(102, 16, 242, 0.1)', color: '#6610f2', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
              <Briefcase size={16} />
            </div>
            <h6 className="mb-0" style={{ fontWeight: 'bold', fontSize: '14px', color: '#6610f2', fontFamily: "'Poppins', sans-serif" }}>Employment Details</h6>
          </div>
          <Row className="mb-3">
            <Col lg="4">
              <FormSelect
                label="Company"
                id="employee_Business_Id"
                name="employee_Business_Id"
                options={businessOptions}
                value={businessOptions.find(option => option.value === form.employee_Business_Id) || null}
                onChange={(selectedOption) => handleSelectFormChange(selectedOption, 'employee_Business_Id')}
                error={errors.employee_Business_Id}
                required={true}
                isDisabled={isCompanyDisabled}
                icon={Building2}
              />
            </Col>
            <Col lg="4">
              <FormSelect
                label="Branch"
                id="employee_Branch_Id"
                name="employee_Branch_Id"
                options={branchOptions}
                value={branchOptions.find(option => option.value === form.employee_Branch_Id) || null}
                onChange={(selectedOption) => handleSelectFormChange(selectedOption, 'employee_Branch_Id')}
                error={errors.employee_Branch_Id}
                required={true}
                isDisabled={isBranchDisabled}
                icon={MapPin}
              />
            </Col>
            <Col lg="4">
              <FormSelect
                label="Department"
                id="employee_Department_ID"
                name="employee_Department_ID"
                options={departmentOptions}
                value={departmentOptions.find(option => option.value === form.employee_Department_ID) || null}
                onChange={(selectedOption) => handleSelectFormChange(selectedOption, 'employee_Department_ID')}
                error={errors.employee_Department_ID}
                required={true}
                icon={Briefcase}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col lg="4">
              <FormSelect
                label="Designation"
                id="employee_Designation_ID"
                name="employee_Designation_ID"
                options={designationOptions}
                value={designationOptions.find(option => option.value === form.employee_Designation_ID) || null}
                onChange={(selectedOption) => handleSelectFormChange(selectedOption, 'employee_Designation_ID')}
                error={errors.employee_Designation_ID}
                required={true}
                icon={Briefcase}
              />
            </Col>
            <Col lg="4">
              <FormInput label="Join Date" id="employee_JoinDate" name="employee_JoinDate" type="date" value={form.employee_JoinDate} onChange={handleFormChange} error={errors.employee_JoinDate} required={true} icon={Calendar} />
            </Col>
            <Col lg="4">
              <FormSelect
                label="Type"
                id="employee_EmploymentType"
                name="employee_EmploymentType"
                value={{ value: form.employee_EmploymentType || 'Permanent', label: form.employee_EmploymentType || 'Permanent' }}
                onChange={(opt) => setForm(prev => ({ ...prev, employee_EmploymentType: opt?.value, employee_ServiceEndDate: opt?.value === 'Permanent' ? '' : prev.employee_ServiceEndDate }))}
                options={[{ value: 'Permanent', label: 'Permanent' }, { value: 'Contract', label: 'Contract' }]}
                icon={Briefcase}
              />
            </Col>
          </Row>

          {form.employee_EmploymentType === 'Contract' && (
            <Row className="mb-3">
              <Col lg="4">
                <FormInput label="End Date" id="employee_ServiceEndDate" name="employee_ServiceEndDate" type="date" value={form.employee_ServiceEndDate} onChange={handleFormChange} error={errors.employee_ServiceEndDate} required={true} icon={Calendar} />
              </Col>
            </Row>
          )}
        </CardBody>
      </Card>

      {/* Salary Information Section */}
      <Card className="mb-3" style={{ border: '2px solid #28a745', borderRadius: '10px', boxShadow: '0 2px 8px rgba(40,167,69,0.08)', overflow: 'hidden' }}>
        <CardBody className="p-3">
          <div className="d-flex align-items-center mb-3 pb-2 border-bottom">
            <div style={{ width: '28px', height: '28px', borderRadius: '5px', backgroundColor: 'rgba(40, 167, 69, 0.1)', color: '#28a745', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
              <CreditCard size={16} />
            </div>
            <h6 className="mb-0" style={{ fontWeight: 'bold', fontSize: '14px', color: '#28a745', fontFamily: "'Poppins', sans-serif" }}>Salary Information</h6>
          </div>
          <Row className="mb-3">
            <Col lg="4"><FormInput label="Basic Salary" id="employee_BS" name="employee_BS" type="number" value={form.employee_BS} onChange={handleFormChange} error={errors.employee_BS} placeholder="Basic Salary" required={true} icon={CreditCard} /></Col>
            <Col lg="4"><FormInput label="HRA" id="employee_HRA" name="employee_HRA" type="number" value={form.employee_HRA} onChange={handleFormChange} error={errors.employee_HRA} placeholder="HRA" required={true} icon={CreditCard} /></Col>
            <Col lg="4"><FormInput label="Transport" id="employee_TA" name="employee_TA" type="number" value={form.employee_TA} onChange={handleFormChange} error={errors.employee_TA} placeholder="Transport" required={false} icon={CreditCard} /></Col>
          </Row>

          <Row className="mb-3">
            <Col lg="3"><FormInput label="Other Allowance" id="employee_OA" name="employee_OA" type="number" value={form.employee_OA} onChange={handleFormChange} error={errors.employee_OA} placeholder="Other" required={false} icon={CreditCard} /></Col>
            <Col lg="3"><FormInput label="Food" id="employee_FoodAllowance" name="employee_FoodAllowance" type="number" value={form.employee_FoodAllowance} onChange={handleFormChange} error={errors.employee_FoodAllowance} placeholder="Food" required={false} icon={CreditCard} /></Col>
            <Col lg="3"><FormInput label="Communication" id="employee_CommunicationAllowance" name="employee_CommunicationAllowance" type="number" value={form.employee_CommunicationAllowance} onChange={handleFormChange} error={errors.employee_CommunicationAllowance} placeholder="Comm." required={false} icon={CreditCard} /></Col>
            <Col lg="3"><FormInput label="Utility" id="employee_UtilityAllowance" name="employee_UtilityAllowance" type="number" value={form.employee_UtilityAllowance} onChange={handleFormChange} error={errors.employee_UtilityAllowance} placeholder="Utility" required={false} icon={CreditCard} /></Col>
          </Row>

          <Row className="mb-3">
            <Col lg="6"><FormInput label="WPS Salary" id="wpsSalary" name="wpsSalary" type="number" value={form.wpsSalary} onChange={handleFormChange} error={errors.wpsSalary} placeholder="WPS Salary" required={true} icon={ShieldCheck} /></Col>
            <Col lg="6"><FormInput label="Gross Salary" id="employee_GS" name="employee_GS" type="number" value={form.employee_GS} className="bg-light fw-bold" readOnly={true} disabled={true} icon={Gem} /></Col>
          </Row>
        </CardBody>
      </Card>

      {/* Documents & Visas Section */}
      <Card className="mb-3" style={{ border: '2px solid #dc3545', borderRadius: '10px', boxShadow: '0 2px 8px rgba(220,53,69,0.08)', overflow: 'hidden' }}>
        <CardBody className="p-3">
          <div className="d-flex align-items-center mb-3 pb-2 border-bottom">
            <div style={{ width: '28px', height: '28px', borderRadius: '5px', backgroundColor: 'rgba(220, 53, 69, 0.1)', color: '#dc3545', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
              <ShieldCheck size={16} />
            </div>
            <h6 className="mb-0" style={{ fontWeight: 'bold', fontSize: '14px', color: '#dc3545', fontFamily: "'Poppins', sans-serif" }}>Documents & Visas</h6>
          </div>
          <Row className="mb-3">
            <Col lg="4"><FormInput label="Passport #" id="employee_PassportNumber" name="employee_PassportNumber" value={form.employee_PassportNumber} onChange={handleFormChange} error={errors.employee_PassportNumber} placeholder="Passport Number" required={true} icon={FileText} /></Col>
            <Col lg="4"><FormInput label="Passport Issue" id="employee_Passport_Date" name="employee_Passport_Date" type="date" value={form.employee_Passport_Date} onChange={handleFormChange} error={errors.employee_Passport_Date} icon={Calendar} /></Col>
            <Col lg="4"><FormInput label="Passport Expiry" id="employee_Passport_ExpDate" name="employee_Passport_ExpDate" type="date" value={form.employee_Passport_ExpDate} onChange={handleFormChange} error={errors.employee_Passport_ExpDate} required={true} icon={Calendar} /></Col>
          </Row>

          <Row className="mb-3">
            <Col lg="4"><FormInput label="Visa #" id="employee_VisaNumber" name="employee_VisaNumber" value={form.employee_VisaNumber} onChange={handleFormChange} error={errors.employee_VisaNumber} placeholder="Visa Number" required={true} icon={FileText} /></Col>
            <Col lg="4"><FormInput label="Visa Issue" id="employee_Visa_Date" name="employee_Visa_Date" type="date" value={form.employee_Visa_Date} onChange={handleFormChange} error={errors.employee_Visa_Date} icon={Calendar} /></Col>
            <Col lg="4"><FormInput label="Visa Expiry" id="employee_Visa_ExpDate" name="employee_Visa_ExpDate" type="date" value={form.employee_Visa_ExpDate} onChange={handleFormChange} error={errors.employee_Visa_ExpDate} required={true} icon={Calendar} /></Col>
          </Row>

          <Row className="mb-3">
            <Col lg="6"><FormInput label="Labour Card #" id="employee_LabourCardNumber" name="employee_LabourCardNumber" value={form.employee_LabourCardNumber} onChange={handleFormChange} error={errors.employee_LabourCardNumber} placeholder="Labour Card Number" required={true} icon={FileText} /></Col>
            <Col lg="6"><FormInput label="Labour Card Expiry" id="employee_LabourCardExpiry" name="employee_LabourCardExpiry" type="date" value={form.employee_LabourCardExpiry} onChange={handleFormChange} error={errors.employee_LabourCardExpiry} required={true} icon={Calendar} /></Col>
          </Row>
        </CardBody>
      </Card>

      {/* Group Benefits Section */}
      <Card className="mb-3" style={{ border: '2px solid #007bff', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,123,255,0.08)', overflow: 'hidden' }}>
        <CardBody className="p-3">
          <div className="d-flex align-items-center mb-3 pb-2 border-bottom">
            <div style={{ width: '28px', height: '28px', borderRadius: '5px', backgroundColor: 'rgba(0, 123, 255, 0.1)', color: '#007bff', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
              <Gem size={16} />
            </div>
            <h6 className="mb-0" style={{ fontWeight: 'bold', fontSize: '14px', color: '#007bff', fontFamily: "'Poppins', sans-serif" }}>Group Benefits</h6>
          </div>
          <Row className="mb-3">
            <Col lg="6">
              <FormBenefitCheckbox
                label="Group Provided Housing"
                checkboxName="employee_HousingProvided"
                amountName="employee_HousingAmount"
                checkboxChecked={form.employee_HousingProvided}
                amountValue={form.employee_HousingAmount}
                onCheckboxChange={(e) => handleFormChange({
                  target: {
                    name: e.target.name,
                    value: e.target.checked
                  }
                })}
                onAmountChange={(e) => handleFormChange({
                  target: {
                    name: e.target.name,
                    value: parseFloat(e.target.value) || 0
                  }
                })}
              />
            </Col>
            <Col lg="6">
              <FormBenefitCheckbox
                label="Group Provided Telephone"
                checkboxName="employee_TelephoneProvided"
                amountName="employee_TelephoneAmount"
                checkboxChecked={form.employee_TelephoneProvided}
                amountValue={form.employee_TelephoneAmount}
                onCheckboxChange={(e) => handleFormChange({
                  target: {
                    name: e.target.name,
                    value: e.target.checked
                  }
                })}
                onAmountChange={(e) => handleFormChange({
                  target: {
                    name: e.target.name,
                    value: parseFloat(e.target.value) || 0
                  }
                })}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col lg="6">
              <FormBenefitCheckbox
                label="Group Provided Food"
                checkboxName="employee_FoodProvided"
                amountName="employee_FoodAmount"
                checkboxChecked={form.employee_FoodProvided}
                amountValue={form.employee_FoodAmount}
                onCheckboxChange={(e) => handleFormChange({
                  target: {
                    name: e.target.name,
                    value: e.target.checked
                  }
                })}
                onAmountChange={(e) => handleFormChange({
                  target: {
                    name: e.target.name,
                    value: parseFloat(e.target.value) || 0
                  }
                })}
              />
            </Col>
            <Col lg="6">
              <FormBenefitCheckbox
                label="Other Group Benefits"
                checkboxName="employee_OtherBenefits"
                amountName="employee_OtherBenefitsAmount"
                checkboxChecked={form.employee_OtherBenefits}
                amountValue={form.employee_OtherBenefitsAmount}
                onCheckboxChange={(e) => handleFormChange({
                  target: {
                    name: e.target.name,
                    value: e.target.checked
                  }
                })}
                onAmountChange={(e) => handleFormChange({
                  target: {
                    name: e.target.name,
                    value: parseFloat(e.target.value) || 0
                  }
                })}
              />
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Image Upload Section */}
      <Card className="mb-3" style={{ border: '2px solid #343a40', borderRadius: '10px', boxShadow: '0 2px 8px rgba(52,58,64,0.08)', overflow: 'hidden' }}>
        <CardBody className="p-3">
          <div className="d-flex align-items-center mb-3 pb-2 border-bottom">
            <div style={{ width: '28px', height: '28px', borderRadius: '5px', backgroundColor: 'rgba(52, 58, 64, 0.1)', color: '#343a40', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
              <User size={16} />
            </div>
            <h6 className="mb-0" style={{ fontWeight: 'bold', fontSize: '14px', color: '#343a40', fontFamily: "'Poppins', sans-serif" }}>Profile Image</h6>
          </div>
          <Row className="mb-3">
            <Col lg="6">
              <FormGroup>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className={errors.employee_ImagePath ? 'is-invalid' : ''}
                  style={{ fontSize: '13px', borderRadius: '5px' }}
                />
                {errors.employee_ImagePath && (
                  <div className="invalid-feedback">
                    {errors.employee_ImagePath}
                  </div>
                )}
                <ImagePreview 
                  file={selectedImage} 
                  existingImage={form.employee_ImagePath} 
                />
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Status & Settings Section */}
      <Card className="mb-3" style={{ border: '2px solid #74788d', borderRadius: '10px', boxShadow: '0 2px 8px rgba(116,120,141,0.08)', overflow: 'hidden' }}>
        <CardBody className="p-3">
          <div className="d-flex align-items-center mb-3 pb-2 border-bottom">
            <div style={{ width: '28px', height: '28px', borderRadius: '5px', backgroundColor: 'rgba(116, 120, 141, 0.1)', color: '#74788d', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
              <Info size={16} />
            </div>
            <h6 className="mb-0" style={{ fontWeight: 'bold', fontSize: '14px', color: '#74788d', fontFamily: "'Poppins', sans-serif" }}>Status & Settings</h6>
          </div>
          <Row className="mb-3">
            <Col lg="3">
              <FormCheckbox
                label="Active Employee"
                id="onWork"
                name="onWork"
                checked={form.onWork}
                onChange={handleFormChange}
                disabled={isEditing && !hasDeletePermission}
              />
            </Col>
            <Col lg="3">
              <FormCheckbox
                label="On Probation"
                id="employee_IsOnProbation"
                name="employee_IsOnProbation"
                checked={form.employee_IsOnProbation}
                onChange={handleFormChange}
              />
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Action Buttons */}
      <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
        <Button
          type="button"
          color="secondary"
          outline
          onClick={onCancel}
          disabled={loading}
          style={{ borderRadius: '5px', padding: '8px 24px', fontSize: '13px', fontWeight: '600' }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          color="primary"
          disabled={loading}
          style={{ borderRadius: '5px', padding: '8px 30px', fontSize: '13px', fontWeight: '600', minWidth: '120px' }}
        >
          {loading ? (
            <React.Fragment><i className="bx bx-loader bx-spin font-size-16 align-middle me-2"></i> Saving...</React.Fragment>
          ) : (
            isEditing ? "Update Employee" : "Save Employee"
          )}
        </Button>
      </div>
    </Form>
  );
};

const defaultEmployeeForm = {
  employee_Id: null,
  employee_Code: "",
  employee_NameEn: "",
  employee_NameAr: "",
  employee_Business_Id: null,
  employee_Department_ID: null,
  employee_Designation_ID: null,
  employee_Branch_Id: null,
  employee_Gender_Id: null,
  employee_Country_Id: null,
  employee_Address1: "",
  employee_Address2: "",
  employee_BirthDate: "",
  employee_JoinDate: "",
  employee_NatID: "",
  employee_NatID_Date: "",
  employee_NatID_ExpDate: "",
  employee_PassportNumber: "",
  employee_Passport_Date: "",
  employee_Passport_ExpDate: "",
  employee_VisaNumber: "",
  employee_Visa_Date: "",
  employee_Visa_ExpDate: "",
  employee_ServiceEndDate: "",
  employee_BS: 0,
  employee_HRA: 0,
  employee_TA: 0,
  employee_OA: 0,
  employee_GS: 0,
  employee_IsOnProbation: true,
  employee_CreatedDate: new Date(),
  employee_CreatedBy: Number(localStorage.getItem("userId")) || 1,
  employee_ModifiedDate: new Date(),
  employee_ModifiedBy: Number(localStorage.getItem("userId")) || 1,
  employee_ImagePath: null,
  employee_Email: "",
  employee_LabourCardNumber: "",
  employee_LabourCardExpiry: "",
  employee_EmploymentType: "Permanent",
  employee_FoodAllowance: 0,
  employee_CommunicationAllowance: 0,
  employee_UtilityAllowance: 0,
  employee_HousingProvided: false,
  employee_TelephoneProvided: false,
  employee_FoodProvided: false,
  employee_OtherBenefits: false,
  employee_HousingAmount: 0,
  employee_TelephoneAmount: 0,
  employee_FoodAmount: 0,
  employee_OtherBenefitsAmount: 0,
  employee_ContactNumber1: "",
  employee_ContactNumber2: "",
  onWork: true,
  emp_No: "",
  wpsSalary: 0,
};

export { defaultEmployeeForm };
export default EmployeeForm;
